import React from 'react';
import { Card, Badge, Table } from 'react-bootstrap';

export interface RiskRule {
  id?: string;
  name?: string;
  score?: number;
}

export interface RiskScoreData {
  score?: number;
  band?: string;
  matched_rules?: RiskRule[];
}

interface RiskScoreCardProps {
  risk?: RiskScoreData | null;
  labels?: string[];
  /** When false the card is rendered collapsed at first. Default: true. */
  defaultOpen?: boolean;
}

// Maps SumSub bands to Bootstrap variants. PROHIBITED gets danger + dark text
// to match its visual weight on the AML review screen.
const BAND_VARIANT: Record<string, { bg: string; text?: 'dark' }> = {
  LOW: { bg: 'success' },
  MEDIUM: { bg: 'warning', text: 'dark' },
  HIGH: { bg: 'danger' },
  PROHIBITED: { bg: 'dark' },
};

const BAND_LABEL_RU: Record<string, string> = {
  LOW: 'Низкий',
  MEDIUM: 'Средний',
  HIGH: 'Высокий',
  PROHIBITED: 'Запрет',
};

const RECOMMENDATION_RU: Record<string, string> = {
  LOW: 'Автоодобрение',
  MEDIUM: 'Ручная очередь + расширенная проверка (EDD)',
  HIGH: 'Рекомендуется отклонение (RETRY)',
  PROHIBITED: 'Отклонить FINAL (FATF call-for-action)',
};

const renderBand = (band?: string) => {
  if (!band) return null;
  const variant = BAND_VARIANT[band] ?? { bg: 'info' };
  const label = BAND_LABEL_RU[band] ?? band;
  return (
    <Badge bg={variant.bg} text={variant.text}>
      {label} ({band})
    </Badge>
  );
};

const RiskScoreCard: React.FC<RiskScoreCardProps> = ({ risk, labels, defaultOpen = true }) => {
  const [open, setOpen] = React.useState(defaultOpen);

  const hasScore = risk && (risk.score !== undefined || risk.band);
  const hasLabels = Array.isArray(labels) && labels.length > 0;

  if (!hasScore && !hasLabels) {
    return (
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            Риск-скоринг
            <Badge bg="secondary" className="ms-2">не вычислен</Badge>
          </span>
        </Card.Header>
        <Card.Body className="text-muted small">
          Скоринг ещё не рассчитан. SumSub присылает score только после завершения review (COMPLETED/ON_HOLD).
        </Card.Body>
      </Card>
    );
  }

  const score = risk?.score ?? 0;
  const band = risk?.band;
  const recommendation = band ? RECOMMENDATION_RU[band] : '';

  // Bar fills from 0 to 100, color follows the band so visual matches text.
  const barColor = band === 'PROHIBITED'
    ? '#212529'
    : band === 'HIGH'
      ? '#dc3545'
      : band === 'MEDIUM'
        ? '#ffc107'
        : '#198754';

  return (
    <Card className="mb-3">
      <Card.Header
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <span className="fw-semibold">
          Риск-скоринг
          {band && <span className="ms-2">{renderBand(band)}</span>}
          {hasScore && (
            <Badge bg="light" text="dark" className="ms-2">
              {score} / 100
            </Badge>
          )}
          {hasLabels && (
            <Badge bg="info" className="ms-2">
              labels: {labels!.length}
            </Badge>
          )}
        </span>
        <span className="text-muted small">{open ? '▼' : '▶'}</span>
      </Card.Header>
      {open && (
        <Card.Body>
          {hasScore && (
            <>
              <div className="d-flex justify-content-between small text-muted mb-1">
                <span>0</span><span>30</span><span>60</span><span>100</span>
              </div>
              <div
                style={{
                  position: 'relative',
                  height: 14,
                  background: '#e9ecef',
                  borderRadius: 7,
                  marginBottom: 8,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(Math.max(score, 0), 100)}%`,
                    height: '100%',
                    background: barColor,
                    transition: 'width 0.3s',
                  }}
                />
              </div>
              {recommendation && (
                <div className="small mb-3">
                  <span className="text-muted">Рекомендация: </span>
                  <span>{recommendation}</span>
                </div>
              )}
            </>
          )}

          {hasLabels && (
            <div className="mb-3">
              <div className="text-muted small text-uppercase mb-1">Risk labels</div>
              <div className="d-flex flex-wrap gap-1">
                {labels!.map(l => (
                  <Badge key={l} bg="warning" text="dark">{l}</Badge>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(risk?.matched_rules) && risk!.matched_rules!.length > 0 && (
            <div>
              <div className="text-muted small text-uppercase mb-1">Сработавшие правила</div>
              <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Описание</th>
                    <th style={{ width: 80 }} className="text-end">Вклад</th>
                  </tr>
                </thead>
                <tbody>
                  {risk!.matched_rules!.map((r, i) => (
                    <tr key={r.id ?? i}>
                      <td><code style={{ fontSize: '0.75rem' }}>{r.id || '—'}</code></td>
                      <td>{r.name || '—'}</td>
                      <td className="text-end">+{r.score ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      )}
    </Card>
  );
};

export default RiskScoreCard;
